import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff } from "./icons";
import { useAuth } from "../context/AuthContext";

export default function AdminLoginPage() {
  const { user, isLoading, login } = useAuth();
  const navigate = useNavigate();
  const [usernameInput, setUsernameInput] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  if (!isLoading && user?.role === "admin") {
    return <Navigate to="/admin" replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);
    const result = await login(usernameInput, password);
    setIsSubmitting(false);
    if (result.ok) {
      navigate("/admin");
    } else {
      setError(result.error);
    }
  };

  const inputClass =
    "border border-steel-300 px-3 py-2.5 text-sm text-ink outline-none focus:border-ink";
  const labelClass = "text-sm font-semibold text-steel-500";

  return (
    <section className="flex min-h-screen items-center bg-steel-50 pb-16 pt-32 md:pt-36">
      <div className="container-industrial">
        <div className="mx-auto grid max-w-5xl grid-cols-1 border border-steel-200 bg-white shadow-sm lg:grid-cols-2">
          <div
            className="relative hidden flex-col justify-end overflow-hidden bg-ink p-10 lg:flex"
            style={{
              backgroundImage:
                "linear-gradient(180deg, rgba(35,31,32,0.35) 0%, rgba(35,31,32,0.85) 100%), url(/assets/sectors/dark-kitchens.jpg)",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <span className="eyebrow mb-4 text-white/70">Панель управления</span>
            <h1 className="font-display uppercase text-3xl font-semibold tracking-[0.3px] leading-[1.1] text-white xl:text-[2.5rem]">
              Управление каталогом
              <span className="block text-white/90">BARCRAFT</span>
            </h1>
          </div>

          <div className="flex flex-col justify-center p-8 md:p-12">
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="mb-8 flex items-center gap-1.5 self-start text-sm font-semibold text-steel-500 hover:text-accent"
            >
              <ArrowLeft size={14} />
              Назад ко входу для клиентов
            </button>

            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <label className="flex flex-col gap-1.5">
                <span className={labelClass}>Логин</span>
                <input
                  required
                  autoFocus
                  type="text"
                  value={usernameInput}
                  onChange={(e) => setUsernameInput(e.target.value)}
                  className={inputClass}
                />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className={labelClass}>Пароль</span>
                <span className="relative">
                  <input
                    required
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`${inputClass} w-full pr-11`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((open) => !open)}
                    aria-pressed={showPassword}
                    aria-label={showPassword ? "Скрыть пароль" : "Показать пароль"}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-steel-400 transition-colors duration-200 ease-engineered hover:text-ink"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </span>
              </label>
              {error && <p className="text-sm text-accent">{error}</p>}
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary mt-2 w-full disabled:opacity-60"
              >
                {isSubmitting ? "…" : "Войти"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
